import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase, slugify } from "@vinoga/db";
import {
  ArrowLeft,
  Calendar,
  GripVertical,
  Image as ImageIcon,
  Package,
  Pin,
  Plus,
  Power,
  Save,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { SlugInput } from "@/components/admin/SlugInput";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

const inputClass =
  "w-full bg-card border border-border rounded-lg px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground transition-colors";
const labelClass = "font-body text-xs font-medium text-muted-foreground mb-1.5 block";
const sectionClass = "bg-card border border-border rounded-xl p-6 shadow-sm space-y-5";

type ProductRef = {
  id: string;
  slug: string;
  sku: string;
  name: string;
};

export function CollectionEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tr = useT();
  const { toast } = useToast();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    slug: "",
    name: "",
    description: "",
    image_url: "",
    image_side: "left" as "left" | "right",
    is_active: true,
    is_homepage_pinned: false,
    date_from: "",
    date_to: "",
    sort_order: 0,
  });

  // Product picker — search products → click "Add", appears in the list
  // below with drag-handle for reordering.
  const [selected, setSelected] = useState<ProductRef[]>([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ProductRef[]>([]);
  const [searching, setSearching] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // Load existing collection + products.
  useEffect(() => {
    if (isNew) return;
    (async () => {
      const { data, error } = await supabase
        .from("collections")
        .select("*")
        .eq("id", id!)
        .single();
      if (error || !data) {
        toast({ title: tr("common.notFound", "Not found"), variant: "destructive" });
        navigate("/collections");
        return;
      }
      const d = data as any;
      setForm({
        slug: d.slug || "",
        name: d.name || "",
        description: d.description || "",
        image_url: d.image_url || "",
        image_side: (d.image_side ?? "left") as "left" | "right",
        is_active: d.is_active ?? true,
        is_homepage_pinned: d.is_homepage_pinned ?? false,
        date_from: d.date_from ? (String(d.date_from).split("T")[0] ?? "") : "",
        date_to: d.date_to ? (String(d.date_to).split("T")[0] ?? "") : "",
        sort_order: d.sort_order ?? 0,
      });

      const { data: cpRows } = await supabase
        .from("collection_products")
        .select("product_id, sort_order")
        .eq("collection_id", id!)
        .order("sort_order", { ascending: true });
      const ids = (cpRows ?? []).map((r: any) => r.product_id);
      if (ids.length > 0) {
        const { data: prodRows } = await supabase
          .from("products")
          .select("id, slug, sku, translations")
          .in("id", ids);
        const byId: Record<string, ProductRef> = {};
        ((prodRows as any[]) ?? []).forEach((p) => {
          byId[p.id] = {
            id: p.id,
            slug: p.slug,
            sku: p.sku,
            name: p.translations?.lv?.name ?? p.slug,
          };
        });
        setSelected(ids.map((pid) => byId[pid]).filter((p): p is ProductRef => !!p));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Debounced product search.
  useEffect(() => {
    if (search.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      const q = `%${search.trim()}%`;
      const { data } = await supabase
        .from("products")
        .select("id, slug, sku, translations")
        .or(`sku.ilike.${q},slug.ilike.${q}`)
        .eq("active", true)
        .limit(20);
      const selectedIds = new Set(selected.map((p) => p.id));
      setResults(
        ((data as any[]) ?? [])
          .filter((p) => !selectedIds.has(p.id))
          .map((p) => ({
            id: p.id,
            slug: p.slug,
            sku: p.sku,
            name: p.translations?.lv?.name ?? p.slug,
          }))
      );
      setSearching(false);
    }, 250);
    return () => clearTimeout(handle);
  }, [search, selected]);

  const addProduct = (p: ProductRef) => {
    setSelected((prev) => (prev.some((x) => x.id === p.id) ? prev : [...prev, p]));
    setResults((prev) => prev.filter((x) => x.id !== p.id));
    setSearch("");
  };
  const removeProduct = (pid: string) =>
    setSelected((prev) => prev.filter((p) => p.id !== pid));

  const reorder = (over: number) => {
    if (dragIdx === null || dragIdx === over) return;
    const next = [...selected];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(over, 0, moved!);
    setSelected(next);
    setDragIdx(over);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: tr("collections.nameRequired", "Name is required"), variant: "destructive" });
      return;
    }
    const slug = form.slug.trim() || slugify(form.name);
    if (!slug) {
      toast({ title: tr("collections.slugRequired", "Slug is required"), variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      slug,
      name: form.name.trim(),
      description: form.description.trim() || null,
      image_url: form.image_url.trim() || null,
      image_side: form.image_side,
      is_active: form.is_active,
      is_homepage_pinned: form.is_homepage_pinned,
      date_from: form.date_from || null,
      date_to: form.date_to || null,
      sort_order: form.sort_order,
    };
    try {
      let collectionId = id;
      if (isNew) {
        const { data, error } = await supabase
          .from("collections")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        collectionId = (data as any).id;
      } else {
        const { error } = await supabase.from("collections").update(payload).eq("id", id!);
        if (error) throw error;
      }

      // Rewrite collection_products to match `selected` order.
      await supabase.from("collection_products").delete().eq("collection_id", collectionId!);
      if (selected.length > 0) {
        await supabase.from("collection_products").insert(
          selected.map((p, idx) => ({
            collection_id: collectionId,
            product_id: p.id,
            sort_order: idx,
          }))
        );
      }
      toast({
        title: isNew
          ? tr("common.created", "Created")
          : tr("common.saved", "Saved"),
      });
      navigate("/collections");
    } catch (err: any) {
      toast({
        title: tr("common.saveFailed", "Save failed"),
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const slugPreview = useMemo(
    () => form.slug.trim() || slugify(form.name),
    [form.slug, form.name]
  );

  if (loading) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {tr("common.loading", "Loading…")}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      <div className="sticky top-0 z-10 -mx-4 flex flex-col gap-4 border-b border-border bg-background/85 px-4 py-4 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:px-0">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/collections")}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              {isNew
                ? tr("collections.newTitle", "New collection")
                : `${tr("collections.editTitle", "Edit collection")}: ${form.name}`}
            </h1>
            <p className="font-body text-sm text-muted-foreground">
              {tr(
                "collections.editorSubtitle",
                "Pick the products + cover art. Pinned collections appear on the homepage."
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/collections")} disabled={saving}>
            {tr("common.cancel", "Cancel")}
          </Button>
          <Button onClick={save} disabled={saving} className="gap-2">
            <Save size={18} />
            {saving
              ? tr("common.saving", "Saving…")
              : isNew
              ? tr("collections.create", "Create collection")
              : tr("common.save", "Save changes")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <Sparkles className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("collections.sectionDetails", "Details")}
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>{tr("collections.fieldName", "Name")} *</label>
                <input
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Pavasara izlase"
                />
              </div>
              <div>
                <label className={labelClass}>{tr("collections.fieldSlug", "Slug")}</label>
                <SlugInput
                  value={form.slug}
                  onChange={(next) => setForm({ ...form, slug: next })}
                  table="collections"
                  excludeId={isNew ? undefined : id}
                  sourceText={form.name}
                  className={inputClass + " font-mono text-xs"}
                  placeholder={slugify(form.name || "pavasara-izlase")}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {tr("collections.slugHint", "Storefront URL:")}{" "}
                  <span className="font-mono">/collections/{slugPreview || "…"}</span>
                </p>
              </div>
              <div>
                <label className={labelClass}>
                  {tr("collections.fieldDescription", "Description")}
                </label>
                <textarea
                  className={inputClass + " resize-y"}
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={tr(
                    "collections.descPlaceholder",
                    "1–3 sentences shown above the product grid"
                  )}
                />
              </div>
            </div>
          </section>

          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <Package className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("collections.sectionProducts", "Products")} ({selected.length})
              </h2>
            </div>

            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                className={inputClass + " pl-9 pr-8"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tr(
                  "collections.searchProducts",
                  "Search products by name / SKU / slug…"
                )}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {search.trim().length >= 2 && (
              <div className="max-h-64 overflow-y-auto rounded-lg border border-border bg-muted/20">
                {searching && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {tr("common.loading", "Loading…")}
                  </p>
                )}
                {!searching && results.length === 0 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {tr("collections.noResults", "No matches.")}
                  </p>
                )}
                {results.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addProduct(p)}
                    className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm hover:bg-muted/30 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">{p.name}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">{p.sku}</p>
                    </div>
                    <Plus size={14} className="text-primary" />
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-1.5">
              {selected.length === 0 && (
                <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
                  {tr("collections.emptyProducts", "No products yet. Search above to add.")}
                </p>
              )}
              {selected.map((p, idx) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    reorder(idx);
                  }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`flex cursor-grab items-center gap-3 rounded-lg border border-border px-3 py-2 active:cursor-grabbing ${
                    dragIdx === idx ? "bg-muted/50" : "bg-card"
                  }`}
                >
                  <GripVertical size={14} className="text-muted-foreground/40" />
                  <span className="w-6 text-xs text-muted-foreground">{idx + 1}.</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">{p.sku}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => removeProduct(p.id)}
                  >
                    <Trash2 size={14} className="text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          </section>

          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <Calendar className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("collections.sectionAvailability", "Availability")}
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {tr("collections.fieldDateFrom", "Show from")}
                </label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.date_from}
                  onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("collections.fieldDateTo", "Show until")}
                </label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.date_to}
                  onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                />
              </div>
            </div>
            <Toggle
              icon={<Power size={18} />}
              label={tr("collections.fieldActive", "Active")}
              hint={tr("collections.activeHint", "Visible on the storefront")}
              checked={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
            />
            <Toggle
              icon={<Pin size={18} />}
              label={tr("collections.fieldPinned", "Pin to homepage")}
              hint={tr(
                "collections.pinnedHint",
                "Renders on the homepage as a side-image + 3×2 grid card"
              )}
              checked={form.is_homepage_pinned}
              onChange={(v) => setForm({ ...form, is_homepage_pinned: v })}
            />
          </section>
        </div>

        <div className="space-y-6">
          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <ImageIcon className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("collections.sectionImage", "Cover image")}
              </h2>
            </div>
            <div>
              <label className={labelClass}>
                {tr("collections.fieldImage", "Cover image")}
              </label>
              <ImageUpload
                value={form.image_url}
                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                bucket="collections"
                preserveAspectRatio
                placeholder={tr("collections.uploadHint", "Drop a cover image here (or click to browse)")}
              />
              <p className="mt-2 text-[11px] text-muted-foreground">
                {tr(
                  "collections.imageHint",
                  "Portrait orientation (≈3:4) works best — renders as a vertical panel next to the product grid.",
                )}
              </p>
              {form.image_url && (
                <details className="mt-3">
                  <summary className="text-[11px] text-muted-foreground cursor-pointer">
                    {tr("collections.showUrl", "Show / paste raw URL")}
                  </summary>
                  <input
                    className={inputClass + " font-mono text-xs mt-2"}
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  />
                </details>
              )}
            </div>
            <div>
              <label className={labelClass}>
                {tr("collections.fieldImageSide", "Image side on homepage card")}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(["left", "right"] as const).map((side) => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => setForm({ ...form, image_side: side })}
                    className={`rounded-lg border p-3 text-xs font-medium ${
                      form.image_side === side
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                  >
                    {side === "left"
                      ? tr("collections.sideLeft", "Left of grid")
                      : tr("collections.sideRight", "Right of grid")}
                  </button>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  icon,
  label,
  hint,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex items-center gap-3">
        <div
          className={`rounded-full p-2 ${
            checked ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          }`}
        >
          {icon}
        </div>
        <div>
          <p className="font-body text-sm font-medium text-foreground">{label}</p>
          <p className="font-body text-xs text-muted-foreground">{hint}</p>
        </div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 cursor-pointer rounded-sm"
      />
    </div>
  );
}
